// Radial Team-Capacity view.
//
// The end-state Kabelo asked for:
//   "A live-state visual representation of our effort, weighted and
//    anchored against S/D KPIs running on the outer perimeter, with SYM
//    point at 12 o'clock. Shape of committed resources (people +
//    committed actions) relative to category actions — at a glance you
//    should know that 80% of resources are on S1:Supermarkets via
//    Ufuoma, Semiloore, Williams."
//
// Layout
// ──────
//   Outer perimeter ring carries every (KPI × category) slot:
//     SYM       at 12 o'clock (no categories — system-wide anchor)
//     S1, S2, S3 fan around the LEFT half  (top → bottom)
//     D1, D2, D3 fan around the RIGHT half (top → bottom)
//
//   Inside the ring, people are placed at the centroid of their
//   commitments — auto-clustered toward where they spend time — with a
//   little local repulsion so labels don't collide.
//
//   Each commitment is drawn as a curved edge from the person to the
//   target (KPI:category) point on the perimeter. Edge weight = number
//   of distinct active commitments on that (person, kpi, category)
//   tuple from the action ledger + partner-rollout static seed.
//
// Reads
// ─────
//   * THICK braid into one perimeter slot = concentration → "this is
//     where the room is pulling".
//   * Empty perimeter slot (faint dot only) = no coverage → risk flag.
//   * Person with a wide fan of thin spokes = distributed attention.
//   * Person with one thick spoke = a single focused workstream.

import { useEffect, useMemo, useRef, useState } from "react";

import { CATEGORIES } from "../../lib/categories";
import {
  MERCHANTS,
  STATUS_COLOR as MERCHANT_STATUS_COLOR,
  STATUS_LABEL as MERCHANT_STATUS_LABEL,
  type Merchant,
  type MerchantStatus,
} from "../../lib/partners-current";
import type { ActionRow } from "../../lib/actions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RadialDatum {
  /** Display name as it appears in the action ledger / track-owners. */
  person: string;
  /** Lower-case KPI code (s1, s2, s3, d1, d2, d3, sym). */
  kpi: string;
  /** Category code from CATEGORIES (e.g. "supermarkets"). Empty/undefined
   *  → person is committed at KPI level, not against a specific category. */
  category?: string;
  /** Active = doing | watch | agreed (in flight or upcoming). Used to
   *  filter out done / disagreed before counting. */
  active: boolean;
}

interface PerimeterSlot {
  id: string;            // "s1:supermarkets" | "sym"
  kpi: string;
  category?: string;
  angle: number;         // degrees, screen convention (0=right, 90=up)
  x: number;
  y: number;
  side: "supply" | "demand" | "sym";
  /** ManCo priority tier of the underlying category — drives dot size
   *  AND its radial offset (P0 = outer "first line", P1 = mid line,
   *  P2 = inner line). SYM has no tier; rendered as outer. */
  tier: 0 | 1 | 2;
}

interface PersonPair {
  a: string;
  b: string;
  /** Number of distinct (kpi, category) slots both a and b touch. */
  shared: number;
  ax: number; ay: number;
  bx: number; by: number;
}

interface MerchantNode {
  id: string;
  name: string;
  category: string;
  status: MerchantStatus;
  priority: boolean;
  x: number;
  y: number;
  angle: number;
}

interface KpiArc {
  kpi: string;           // "S1" etc.
  side: "supply" | "demand" | "sym";
  labelAngle: number;    // angle of the KPI label (midpoint of arc)
  labelX: number;
  labelY: number;
}

interface PersonNode {
  name: string;
  total: number;         // total commitment weight across all slots
  x: number;
  y: number;
  // pre-computed weighted target for placement
  tx: number;
  ty: number;
}

interface Edge {
  person: string;
  slotId: string;
  weight: number;
  px: number; py: number;  // person position
  sx: number; sy: number;  // slot position
}

// ---------------------------------------------------------------------------
// Aggregator — actions → (person, slot) weights
// ---------------------------------------------------------------------------

export function buildRadialData(actions: ActionRow[]): RadialDatum[] {
  const out: RadialDatum[] = [];
  for (const a of actions) {
    if (!a.owner) continue;
    const owners = splitOwners(a.owner);
    const active =
      a.status === "doing" || a.status === "watch";
    // We treat 'done' and 'blocked' as NOT active for resource purposes:
    // done = no longer carrying, blocked = blocked elsewhere not on
    // category. 'disagree' kind is filtered too.
    if (a.kind === "disagree") continue;

    for (const impact of a.impacts ?? []) {
      const [kpiRaw, catRaw] = impact.split(":");
      const kpi = (kpiRaw ?? "").toLowerCase();
      const category = (catRaw ?? "").toLowerCase() || undefined;
      if (!kpi) continue;
      for (const owner of owners) {
        out.push({ person: owner, kpi, category, active });
      }
    }
  }
  return out;
}

function splitOwners(raw: string): string[] {
  // Owners can be "Williams" or "PSL + Ife" or "Kabelo · Adaobi" — split
  // on common delimiters and trim.
  return raw
    .split(/\s*[·•,&]+\s*|\s*\+\s*/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Geometry — outer ring layout
// ---------------------------------------------------------------------------

const KPIS: Array<{ code: string; side: "supply" | "demand" | "sym" }> = [
  { code: "sym", side: "sym" },
  { code: "s1", side: "supply" },
  { code: "s2", side: "supply" },
  { code: "s3", side: "supply" },
  { code: "d1", side: "demand" },
  { code: "d2", side: "demand" },
  { code: "d3", side: "demand" },
];

// Angular range (degrees, standard math convention: 0=right, 90=up).
// SYM occupies a small slot at the top centred on 90°. Supply runs from
// 105° anticlockwise down to 255° (left half). Demand runs from 75°
// clockwise to -75° (right half).
const SYM_ANGLE = 90;
const SUPPLY_START = 105;   // just CCW of SYM
const SUPPLY_END = 255;     // just CCW of 6 o'clock
const DEMAND_START = 75;    // just CW of SYM
const DEMAND_END = -75;     // (CW past 0° → -75° == 285°)

function kpiArcRange(code: string): [number, number] {
  if (code === "sym") return [SYM_ANGLE - 6, SYM_ANGLE + 6];
  // Supply spans SUPPLY_START → SUPPLY_END (anticlockwise) split into 3
  if (code === "s1" || code === "s2" || code === "s3") {
    const span = SUPPLY_END - SUPPLY_START; // 150°
    const each = span / 3;
    const idx = { s1: 0, s2: 1, s3: 2 }[code]!;
    return [SUPPLY_START + idx * each, SUPPLY_START + (idx + 1) * each];
  }
  // Demand spans DEMAND_START → DEMAND_END (clockwise) split into 3
  const span = DEMAND_START - DEMAND_END; // 150°
  const each = span / 3;
  const idx = { d1: 0, d2: 1, d3: 2 }[code]!;
  return [DEMAND_START - idx * each, DEMAND_START - (idx + 1) * each];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  data: RadialDatum[];
  /** When supplied, only count active commitments (default true). */
  onlyActive?: boolean;
}

export function RadialCapacity({ data, onlyActive = true }: Props) {
  const [size, setSize] = useState({ w: 900, h: 900 });
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Track container size so the SVG resizes on window resize.
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      const w = Math.max(540, Math.floor(r.width));
      const h = Math.max(540, Math.floor(w * 0.9));
      setSize({ w, h });
    });
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const { w, h } = size;
  const cx = w / 2;
  const cy = h / 2;
  const outerR = Math.min(w, h) * 0.40;
  const labelR = outerR + 22;
  const kpiLabelR = outerR + 56;

  const slots = useMemo(() => computeSlots(cx, cy, outerR), [cx, cy, outerR]);
  const kpiArcs = useMemo(
    () => computeKpiArcs(cx, cy, kpiLabelR),
    [cx, cy, kpiLabelR],
  );
  // Merchant ring sits inside the category ring, outside the people
  // zone. Computed from the static priority + standard merchant list.
  const merchants = useMemo(
    () => computeMerchants(cx, cy, outerR, slots),
    [cx, cy, outerR, slots],
  );

  // Aggregate person → slot weights + person ↔ person collaboration.
  const { peopleNodes, edges, collabPairs, totalWeight, perSlotWeight } =
    useMemo(
      () => layoutPeopleAndEdges(data, slots, cx, cy, outerR, onlyActive),
      [data, slots, cx, cy, outerR, onlyActive],
    );

  const [hover, setHover] = useState<
    | { kind: "person"; name: string }
    | { kind: "slot"; id: string }
    | { kind: "merchant"; id: string }
    | null
  >(null);

  const focusName =
    hover?.kind === "person" ? hover.name : null;
  const focusSlot =
    hover?.kind === "slot" ? hover.id : null;
  const focusMerchant =
    hover?.kind === "merchant" ? hover.id : null;

  const tooltipText = useMemo(() => {
    if (focusName) {
      const pn = peopleNodes.find((p) => p.name === focusName);
      if (!pn) return "";
      const ownedSlots = edges
        .filter((e) => e.person === focusName)
        .sort((a, b) => b.weight - a.weight);
      const top = ownedSlots
        .slice(0, 4)
        .map((e) => `${slotLabel(e.slotId)} (${e.weight})`)
        .join(" · ");
      const collaborators = collabPairs
        .filter((p) => p.a === focusName || p.b === focusName)
        .sort((a, b) => b.shared - a.shared)
        .slice(0, 4)
        .map((p) => `${p.a === focusName ? p.b : p.a} (${p.shared})`)
        .join(", ");
      const collabPart = collaborators ? ` · overlaps: ${collaborators}` : "";
      return `${focusName} · ${pn.total} commitment${pn.total === 1 ? "" : "s"} — ${top}${collabPart}`;
    }
    if (focusSlot) {
      const ownersHere = edges
        .filter((e) => e.slotId === focusSlot)
        .sort((a, b) => b.weight - a.weight);
      const list = ownersHere
        .slice(0, 5)
        .map((e) => `${e.person} (${e.weight})`)
        .join(" · ");
      const total = ownersHere.reduce((s, e) => s + e.weight, 0);
      return `${slotLabel(focusSlot)} · ${total} resource${total === 1 ? "" : "s"} — ${list || "no commitments"}`;
    }
    if (focusMerchant) {
      const m = merchants.find((x) => x.id === focusMerchant);
      if (!m) return "";
      const catLabel = (CATEGORIES.find((c) => c.code === m.category)?.label ?? m.category);
      return `${m.name} · ${catLabel} · ${MERCHANT_STATUS_LABEL[m.status]}${m.priority ? "" : " (standard list)"}`;
    }
    return null;
  }, [focusName, focusSlot, focusMerchant, peopleNodes, edges, collabPairs, merchants]);

  return (
    <div ref={wrapRef} style={wrap}>
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: "block" }}
      >
        {/* Faint reference ring */}
        <circle
          cx={cx}
          cy={cy}
          r={outerR}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={1}
        />

        {/* Side glyphs — light purple/pink arcs hinting at supply / demand */}
        <SideArc
          cx={cx}
          cy={cy}
          r={outerR + 10}
          startDeg={SUPPLY_START}
          endDeg={SUPPLY_END}
          color="rgba(94,74,168,0.45)"
        />
        <SideArc
          cx={cx}
          cy={cy}
          r={outerR + 10}
          startDeg={DEMAND_START}
          endDeg={DEMAND_END}
          color="rgba(209,75,143,0.45)"
          reverse
        />

        {/* KPI labels are now baked into the KPI-centre slot circles
            below, so this outer labelling layer is redundant. Kept as
            a no-op to make the change diff easier to revert if we
            decide we want both. */}
        {void kpiArcs}

        {/* Perimeter slots — every (kpi × category) point gets a dot.
            Size encodes priority TIER (P0 large, P1 medium, P2 small).
            Brightness encodes how much committed resource is on that
            slot. Empty slots stay as faint ticks so coverage gaps
            remain visible. */}
        {slots.map((s) => {
          const weight = perSlotWeight.get(s.id) ?? 0;
          const isFocused = focusSlot === s.id;
          const isInFocusPerson =
            focusName && edges.some((e) => e.person === focusName && e.slotId === s.id);
          const lit = weight > 0 || isFocused || isInFocusPerson;
          // KPI-centre = slot keyed by kpi only (no category). These
          // are the strategic anchors for KPI-only commitments (track
          // owners not yet tagged with a specific category). Drawn as
          // an open ring + KPI letter inside so the eye groups them
          // separately from category dots.
          const isKpiCentre =
            !s.category && s.id !== "sym";
          const tierBase = s.tier === 0 ? 6 : s.tier === 1 ? 4.5 : 3.2;
          const r = isKpiCentre
            ? 10 + Math.min(6, weight * 0.5)
            : lit
              ? tierBase + Math.min(5, weight * 0.6)
              : tierBase * 0.55;
          const op = lit ? 0.95 : 0.35;
          return (
            <g key={s.id}>
              {isKpiCentre ? (
                <>
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={r}
                    fill={lit ? slotColor(s.side, weight) : "transparent"}
                    fillOpacity={lit ? 0.18 : 0}
                    stroke={slotColor(s.side, weight)}
                    strokeOpacity={lit ? 0.85 : 0.5}
                    strokeWidth={isFocused ? 2.5 : 1.6}
                    style={{ cursor: "default" }}
                    onMouseEnter={() => setHover({ kind: "slot", id: s.id })}
                    onMouseLeave={() =>
                      setHover((h) => (h?.kind === "slot" && h.id === s.id ? null : h))
                    }
                  />
                  <text
                    x={s.x}
                    y={s.y}
                    fontFamily="SF Mono, Menlo, monospace"
                    fontSize={10}
                    fontWeight={700}
                    letterSpacing={1.5}
                    fill={lit ? slotColor(s.side, weight) : "rgba(255,255,255,0.55)"}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    pointerEvents="none"
                  >
                    {s.kpi.toUpperCase()}
                  </text>
                </>
              ) : (
                <>
                  <circle
                    cx={s.x}
                    cy={s.y}
                    r={r}
                    fill={slotColor(s.side, weight)}
                    opacity={op}
                    stroke={isFocused ? "#fff" : "none"}
                    strokeWidth={isFocused ? 1.5 : 0}
                    style={{ cursor: "default" }}
                    onMouseEnter={() => setHover({ kind: "slot", id: s.id })}
                    onMouseLeave={() =>
                      setHover((h) => (h?.kind === "slot" && h.id === s.id ? null : h))
                    }
                  />
                  {weight > 0 && (
                    <text
                      x={cx + Math.cos(toRad(s.angle)) * labelR}
                      y={cy - Math.sin(toRad(s.angle)) * labelR}
                      fontSize={8.5}
                      fontFamily="SF Mono, Menlo, monospace"
                      fill="rgba(255,255,255,0.5)"
                      textAnchor={textAnchorForAngle(s.angle)}
                      dominantBaseline="middle"
                      pointerEvents="none"
                    >
                      {shortCategoryLabel(s.category)}
                    </text>
                  )}
                </>
              )}
            </g>
          );
        })}

        {/* Merchant ring — every priority + standard merchant sits on
            its own ring inside the category perimeter. Status colour
            reads at a glance: green = onboarded, amber = in motion,
            grey = upstream. Priority merchants are larger + opaque;
            standard list slightly smaller + dimmer so the priority
            band dominates. */}
        {merchants.map((m) => {
          const isHover =
            hover?.kind === "merchant" && hover.id === m.id;
          const isInFocusSlot =
            focusSlot &&
            focusSlot !== "sym" &&
            focusSlot.endsWith(`:${m.category}`);
          const dimmed = focusName || (focusSlot && !isInFocusSlot && !isHover);
          const baseR = m.priority ? 4 : 2.8;
          const r = isHover ? baseR + 2 : baseR;
          const fill = MERCHANT_STATUS_COLOR[m.status];
          return (
            <circle
              key={m.id}
              cx={m.x}
              cy={m.y}
              r={r}
              fill={fill}
              fillOpacity={
                dimmed ? 0.18 : m.priority ? 0.95 : 0.65
              }
              stroke={isHover ? "#fff" : "rgba(255,255,255,0.35)"}
              strokeWidth={isHover ? 1.4 : 0.5}
              style={{ cursor: "default" }}
              onMouseEnter={() =>
                setHover({ kind: "merchant", id: m.id })
              }
              onMouseLeave={() =>
                setHover((h) =>
                  h?.kind === "merchant" && h.id === m.id ? null : h,
                )
              }
            />
          );
        })}

        {/* Person → slot edges — kept as a quiet BACKGROUND trace so
            you can still see "this person is on that slot" without it
            dominating the visual. Foreground story lives in positions
            and the person↔person collaboration edges below. */}
        {edges.map((e) => {
          const dimmed =
            (focusName && e.person !== focusName) ||
            (focusSlot && e.slotId !== focusSlot);
          const focused =
            (focusName && e.person === focusName) ||
            (focusSlot && e.slotId === focusSlot);
          // Default thin; only bright on hover.
          const sw = focused
            ? Math.max(1.2, Math.min(4, e.weight * 1.1))
            : Math.max(0.4, Math.min(1.2, e.weight * 0.35));
          const op = focused ? 0.7 : dimmed ? 0.04 : 0.16;
          return (
            <path
              key={`${e.person}-${e.slotId}`}
              d={curvePath(e.px, e.py, e.sx, e.sy, cx, cy)}
              stroke={edgeStroke(e.slotId, e.weight)}
              strokeWidth={sw}
              strokeOpacity={op}
              fill="none"
              pointerEvents="none"
            />
          );
        })}

        {/* Person ↔ person collaboration edges — the foreground signal.
            Lines drawn between people who share any (kpi:category)
            work; thickness = shared count. This is the "where streams
            overlap" read. */}
        {collabPairs.map((p) => {
          const dimmed =
            focusName && p.a !== focusName && p.b !== focusName;
          const focused = focusName && (p.a === focusName || p.b === focusName);
          const sw = Math.max(1.2, Math.min(4.5, p.shared * 1.3));
          return (
            <line
              key={`${p.a}-${p.b}`}
              x1={p.ax}
              y1={p.ay}
              x2={p.bx}
              y2={p.by}
              stroke="rgba(255,255,255,0.92)"
              strokeWidth={sw}
              strokeOpacity={focused ? 0.85 : dimmed ? 0.05 : 0.32}
              strokeLinecap="round"
              pointerEvents="none"
            />
          );
        })}

        {/* People — circle + label */}
        {peopleNodes.map((p) => {
          const r = 5 + Math.min(11, Math.sqrt(p.total) * 2.4);
          const focused = focusName === p.name;
          const dimmed = focusName && !focused;
          const opacity = dimmed ? 0.28 : 1;
          return (
            <g
              key={p.name}
              transform={`translate(${p.x},${p.y})`}
              onMouseEnter={() => setHover({ kind: "person", name: p.name })}
              onMouseLeave={() =>
                setHover((h) =>
                  h?.kind === "person" && h.name === p.name ? null : h,
                )
              }
              style={{ cursor: "default" }}
            >
              <circle
                r={r}
                fill="#7a6bd0"
                opacity={opacity * 0.9}
                stroke={focused ? "#fff" : "rgba(255,255,255,0.45)"}
                strokeWidth={focused ? 1.5 : 1}
              />
              <text
                x={0}
                y={r + 12}
                fontSize={11}
                fontWeight={focused ? 700 : 500}
                fontFamily="-apple-system, system-ui, sans-serif"
                fill={`rgba(255,255,255,${opacity})`}
                textAnchor="middle"
              >
                {p.name}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip rail + merchant status legend */}
      <div style={tooltipBar}>
        {tooltipText
          ? tooltipText
          : `${totalWeight} committed resources · ${collabPairs.length} overlap-pair${collabPairs.length === 1 ? "" : "s"} · ${merchants.length} merchants (${merchants.filter((m) => m.status === "onboarded").length} onboarded, ${merchants.filter((m) => m.status === "integrating").length} integrating).`}
      </div>
      <div style={legendRow}>
        <span style={legendLabel}>MERCHANTS</span>
        {(["onboarded", "integrating", "reviewing-sla", "mgmt-alignment", "value-prop", "prospect"] as MerchantStatus[]).map(
          (s) => (
            <span key={s} style={legendItem}>
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 9,
                  background: MERCHANT_STATUS_COLOR[s],
                  display: "inline-block",
                  marginRight: 5,
                }}
              />
              {MERCHANT_STATUS_LABEL[s]}
            </span>
          ),
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

// Radial offset per priority tier — pulls P1 / P2 dots inward from the
// outer "first line" so the perimeter reads as concentric rings of
// importance. The gap is intentionally readable (10 px between rings).
const TIER_OFFSET: Record<0 | 1 | 2, number> = { 0: 0, 1: 12, 2: 22 };

function categoryTier(code: string): 0 | 1 | 2 {
  const c = CATEGORIES.find((x) => x.code === code);
  if (!c) return 2;
  if (c.priority === "p0") return 0;
  if (c.priority === "p1") return 1;
  return 2;
}

function computeSlots(cx: number, cy: number, r: number): PerimeterSlot[] {
  const slots: PerimeterSlot[] = [];
  for (const { code, side } of KPIS) {
    if (code === "sym") {
      slots.push({
        id: "sym",
        kpi: "sym",
        angle: SYM_ANGLE,
        x: cx + Math.cos(toRad(SYM_ANGLE)) * r,
        y: cy - Math.sin(toRad(SYM_ANGLE)) * r,
        side,
        tier: 0,
      });
      continue;
    }
    const [a0, a1] = kpiArcRange(code);
    const cats = CATEGORIES.map((c) => c.code);
    const n = cats.length;
    const total = a1 - a0;
    const gutter = Math.abs(total) * 0.08;
    const innerStart = a0 + Math.sign(total) * gutter;
    const innerEnd = a1 - Math.sign(total) * gutter;
    const step = (innerEnd - innerStart) / Math.max(1, n - 1);
    cats.forEach((cat, i) => {
      const ang = innerStart + step * i;
      const tier = categoryTier(cat);
      const slotR = r - TIER_OFFSET[tier];
      slots.push({
        id: `${code}:${cat}`,
        kpi: code,
        category: cat,
        angle: ang,
        x: cx + Math.cos(toRad(ang)) * slotR,
        y: cy - Math.sin(toRad(ang)) * slotR,
        side,
        tier,
      });
    });

    // KPI-CENTER slot — the anchor for commitments tagged with just
    // the KPI code (`[s1]`) and no category. Without this they get
    // silently dropped and the radial reads D-heavy because category
    // leads dominate while supply track-owners (most of them KPI-only)
    // never register. Placed slightly OUTSIDE the category ring at
    // the arc midpoint so it visually claims the KPI's territory.
    const midAngle = (a0 + a1) / 2;
    const centerR = r + 18;
    slots.push({
      id: code,
      kpi: code,
      angle: midAngle,
      x: cx + Math.cos(toRad(midAngle)) * centerR,
      y: cy - Math.sin(toRad(midAngle)) * centerR,
      side,
      tier: 0,
    });
  }
  return slots;
}

// Merchant ring — every priority merchant from partners-current sits on
// an inner concentric ring at radius outerR - MERCHANT_OFFSET, angled
// to its S1:category slot. The 8 supermarket merchants form a tight
// fan inside S1:Supermarkets, and so on. Status drives colour:
//   onboarded   → dark green
//   integrating → bright green
//   mgmt-align  → amber
//   reviewing-SLA → orange
//   value-prop  → grey
//   prospect    → light grey
// Standard (non-priority) merchants render slightly smaller + dimmer
// so the priority list dominates the read.
const MERCHANT_RING_OFFSET = 30;
const MERCHANT_FAN_DEG_PER = 0.9; // angular space allocated per merchant
const MERCHANT_FAN_MAX_DEG = 5.5;

function computeMerchants(
  cx: number,
  cy: number,
  outerR: number,
  slots: PerimeterSlot[],
): MerchantNode[] {
  const out: MerchantNode[] = [];
  const merchantR = outerR - MERCHANT_RING_OFFSET;
  // Anchor merchants to their S1:category slot (supply side, where the
  // onboarding work happens). Single ring keeps the read clean.
  const slotByCat = new Map<string, PerimeterSlot>();
  for (const s of slots) {
    if (s.kpi === "s1" && s.category) slotByCat.set(s.category, s);
  }
  const byCategory = new Map<string, Merchant[]>();
  for (const m of MERCHANTS) {
    if (!byCategory.has(m.category)) byCategory.set(m.category, []);
    byCategory.get(m.category)!.push(m);
  }
  for (const [catCode, merchants] of byCategory) {
    const slot = slotByCat.get(catCode);
    if (!slot) continue;
    // Sort merchants: priority first, then onboarded ones, then the
    // pipeline ordered by status closeness to onboarded. Keeps the
    // green cluster visually adjacent inside each category.
    const sorted = [...merchants].sort((a, b) => {
      const pa = a.standard ? 1 : 0;
      const pb = b.standard ? 1 : 0;
      if (pa !== pb) return pa - pb;
      return statusOrder(a.status) - statusOrder(b.status);
    });
    const n = sorted.length;
    const spread = Math.min(MERCHANT_FAN_MAX_DEG, n * MERCHANT_FAN_DEG_PER);
    sorted.forEach((m, i) => {
      const offset = n === 1 ? 0 : (i / (n - 1) - 0.5) * spread;
      const ang = slot.angle + offset;
      out.push({
        id: m.id,
        name: m.name,
        category: catCode,
        status: m.status,
        priority: !m.standard,
        angle: ang,
        x: cx + Math.cos(toRad(ang)) * merchantR,
        y: cy - Math.sin(toRad(ang)) * merchantR,
      });
    });
  }
  return out;
}

function statusOrder(s: MerchantStatus): number {
  const order: Record<MerchantStatus, number> = {
    onboarded: 0,
    integrating: 1,
    "reviewing-sla": 2,
    "mgmt-alignment": 3,
    "value-prop": 4,
    prospect: 5,
  };
  return order[s] ?? 99;
}

function computeKpiArcs(cx: number, cy: number, r: number): KpiArc[] {
  return KPIS.map(({ code, side }) => {
    if (code === "sym") {
      return {
        kpi: code,
        side,
        labelAngle: SYM_ANGLE,
        labelX: cx + Math.cos(toRad(SYM_ANGLE)) * r,
        labelY: cy - Math.sin(toRad(SYM_ANGLE)) * r,
      };
    }
    const [a0, a1] = kpiArcRange(code);
    const mid = (a0 + a1) / 2;
    return {
      kpi: code,
      side,
      labelAngle: mid,
      labelX: cx + Math.cos(toRad(mid)) * r,
      labelY: cy - Math.sin(toRad(mid)) * r,
    };
  });
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

function textAnchorForAngle(deg: number): "start" | "middle" | "end" {
  // Slot to the right of centre → label sits to the right → text starts
  // (left-aligned). Slot to the left → ends right-aligned.
  const x = Math.cos(toRad(deg));
  if (x > 0.25) return "start";
  if (x < -0.25) return "end";
  return "middle";
}

function slotColor(side: PerimeterSlot["side"], weight: number): string {
  if (side === "sym") return weight > 0 ? "#e8e6ef" : "rgba(232,230,239,0.55)";
  if (side === "supply") return weight > 0 ? "#a89bd9" : "rgba(168,155,217,0.6)";
  return weight > 0 ? "#e89db8" : "rgba(232,157,184,0.6)";
}

function edgeStroke(slotId: string, _weight: number): string {
  if (slotId.startsWith("s")) return "rgba(168,155,217,0.95)";
  if (slotId.startsWith("d")) return "rgba(232,157,184,0.95)";
  return "rgba(232,230,239,0.95)";
}

function curvePath(
  px: number,
  py: number,
  sx: number,
  sy: number,
  cx: number,
  cy: number,
): string {
  // Cubic Bezier that pulls toward the centre so edges bundle visually.
  const c1x = px + (cx - px) * 0.55;
  const c1y = py + (cy - py) * 0.55;
  const c2x = sx + (cx - sx) * 0.55;
  const c2y = sy + (cy - sy) * 0.55;
  return `M${px} ${py} C ${c1x} ${c1y} ${c2x} ${c2y} ${sx} ${sy}`;
}

function shortCategoryLabel(code: string | undefined): string {
  if (!code) return "";
  const c = CATEGORIES.find((x) => x.code === code);
  if (!c) return code;
  // Strip "Retail · " or " & ..." for compact perimeter labels.
  return c.label.replace(/^Retail · /, "").replace(/ &.*/, "").slice(0, 16);
}

function slotLabel(id: string): string {
  if (id === "sym") return "SYM";
  const [kpi, cat] = id.split(":");
  if (!cat) return kpi!.toUpperCase();
  const meta = CATEGORIES.find((c) => c.code === cat);
  return `${kpi!.toUpperCase()}:${(meta?.label ?? cat).replace(/^Retail · /, "")}`;
}

// ---------------------------------------------------------------------------
// People + edges layout
// ---------------------------------------------------------------------------

function layoutPeopleAndEdges(
  data: RadialDatum[],
  slots: PerimeterSlot[],
  cx: number,
  cy: number,
  outerR: number,
  onlyActive: boolean,
): {
  peopleNodes: PersonNode[];
  edges: Edge[];
  collabPairs: PersonPair[];
  totalWeight: number;
  perSlotWeight: Map<string, number>;
} {
  const slotById = new Map(slots.map((s) => [s.id, s]));

  // Aggregate weight per (person, slotId).
  const tally = new Map<string, Map<string, number>>();
  for (const d of data) {
    if (onlyActive && !d.active) continue;
    const slotId = d.category ? `${d.kpi}:${d.category}` : d.kpi;
    if (!slotById.has(slotId)) continue;
    if (!tally.has(d.person)) tally.set(d.person, new Map());
    const inner = tally.get(d.person)!;
    inner.set(slotId, (inner.get(slotId) ?? 0) + 1);
  }

  // Anchor each person near their HIGHEST-weight slot (their "primary
  // workstream") rather than the diffuse centroid of all their work.
  // innerMaxR sits BELOW the merchant ring (outerR - 30) so people
  // never visually collide with the merchant dots above them.
  const innerMaxR = outerR * 0.56;
  const innerMinR = outerR * 0.22;
  const rawPositions: Array<{
    name: string;
    x: number;
    y: number;
    total: number;
    primarySlot: string;
    slotIds: Set<string>;
  }> = [];

  for (const [person, slotWeights] of tally) {
    let primarySlotId = "";
    let bestW = -1;
    let total = 0;
    for (const [slotId, w] of slotWeights) {
      total += w;
      if (w > bestW) {
        bestW = w;
        primarySlotId = slotId;
      }
    }
    const primary = slotById.get(primarySlotId);
    if (!primary) continue;
    // Position 65% of the way from centre to the primary slot.
    const dx = primary.x - cx;
    const dy = primary.y - cy;
    const d = Math.sqrt(dx * dx + dy * dy) || 1;
    const want = Math.min(innerMaxR, Math.max(innerMinR, d * 0.65));
    rawPositions.push({
      name: person,
      x: cx + (dx / d) * want,
      y: cy + (dy / d) * want,
      total,
      primarySlot: primarySlotId,
      slotIds: new Set(slotWeights.keys()),
    });
  }

  // Person-pair collaboration: count distinct shared slots per pair.
  // Pairs with ≥ 1 shared slot become an attraction force (and a
  // rendered edge below).
  const pairs: Array<{ a: number; b: number; shared: number }> = [];
  for (let i = 0; i < rawPositions.length; i++) {
    for (let j = i + 1; j < rawPositions.length; j++) {
      const A = rawPositions[i]!.slotIds;
      const B = rawPositions[j]!.slotIds;
      let shared = 0;
      // Iterate the smaller set for speed.
      const [small, large] = A.size <= B.size ? [A, B] : [B, A];
      for (const s of small) if (large.has(s)) shared++;
      if (shared > 0) pairs.push({ a: i, b: j, shared });
    }
  }

  // Force-relaxation: collaborative attraction + repulsion + soft pull
  // back toward each person's primary anchor.
  const ITER = 200;
  const MIN_SEP = 58;
  const anchor = rawPositions.map((p) => ({ x: p.x, y: p.y }));

  for (let it = 0; it < ITER; it++) {
    const cool = 1 - it / ITER; // anneal: stronger early, gentler late

    // 1. Repulsion (collision avoidance) on every pair.
    for (let i = 0; i < rawPositions.length; i++) {
      const a = rawPositions[i]!;
      for (let j = i + 1; j < rawPositions.length; j++) {
        const b = rawPositions[j]!;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
        if (dist < MIN_SEP) {
          const push = ((MIN_SEP - dist) / 2) * cool;
          const nx = dx / dist;
          const ny = dy / dist;
          a.x -= nx * push;
          a.y -= ny * push;
          b.x += nx * push;
          b.y += ny * push;
        }
      }
    }

    // 2. Collaborative attraction — pull pairs with shared work
    //    toward each other, force ∝ sqrt(shared).
    for (const pr of pairs) {
      const A = rawPositions[pr.a]!;
      const B = rawPositions[pr.b]!;
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
      // Only pull if they're farther than the comfortable separation —
      // we don't want to override repulsion.
      if (dist > MIN_SEP * 1.05) {
        const k = Math.sqrt(pr.shared) * 0.018 * cool;
        const nx = dx / dist;
        const ny = dy / dist;
        const pull = (dist - MIN_SEP) * k;
        A.x += nx * pull;
        A.y += ny * pull;
        B.x -= nx * pull;
        B.y -= ny * pull;
      }
    }

    // 3. Mild anchor pull — every person remembers where their primary
    //    workstream is. Keeps the cluster from drifting away from the
    //    slot it represents.
    for (let i = 0; i < rawPositions.length; i++) {
      const p = rawPositions[i]!;
      const ax = anchor[i]!.x;
      const ay = anchor[i]!.y;
      p.x += (ax - p.x) * 0.012 * cool;
      p.y += (ay - p.y) * 0.012 * cool;
    }

    // 4. Clip to inner cap so nobody drifts onto the perimeter.
    for (let i = 0; i < rawPositions.length; i++) {
      const p = rawPositions[i]!;
      const dx0 = p.x - cx;
      const dy0 = p.y - cy;
      const d0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
      if (d0 > innerMaxR) {
        const k = innerMaxR / d0;
        p.x = cx + dx0 * k;
        p.y = cy + dy0 * k;
      } else if (d0 < innerMinR && d0 > 0) {
        const k = innerMinR / d0;
        p.x = cx + dx0 * k;
        p.y = cy + dy0 * k;
      }
    }
  }

  // Build final node + edge structures.
  const peopleNodes: PersonNode[] = rawPositions.map((p) => ({
    name: p.name,
    total: p.total,
    x: p.x,
    y: p.y,
    tx: p.x,
    ty: p.y,
  }));
  const positionByName = new Map(peopleNodes.map((p) => [p.name, p]));

  const edges: Edge[] = [];
  const perSlotWeight = new Map<string, number>();
  for (const [person, slotWeights] of tally) {
    const pn = positionByName.get(person);
    if (!pn) continue;
    for (const [slotId, w] of slotWeights) {
      const slot = slotById.get(slotId)!;
      edges.push({
        person,
        slotId,
        weight: w,
        px: pn.x,
        py: pn.y,
        sx: slot.x,
        sy: slot.y,
      });
      perSlotWeight.set(slotId, (perSlotWeight.get(slotId) ?? 0) + w);
    }
  }

  const collabPairs: PersonPair[] = pairs.map((pr) => {
    const A = peopleNodes[pr.a]!;
    const B = peopleNodes[pr.b]!;
    return {
      a: A.name,
      b: B.name,
      shared: pr.shared,
      ax: A.x,
      ay: A.y,
      bx: B.x,
      by: B.y,
    };
  });

  const totalWeight = edges.reduce((s, e) => s + e.weight, 0);
  return { peopleNodes, edges, collabPairs, totalWeight, perSlotWeight };
}

// ---------------------------------------------------------------------------
// Sub-component: side arc decoration
// ---------------------------------------------------------------------------

function SideArc({
  cx,
  cy,
  r,
  startDeg,
  endDeg,
  color,
  reverse,
}: {
  cx: number;
  cy: number;
  r: number;
  startDeg: number;
  endDeg: number;
  color: string;
  reverse?: boolean;
}) {
  const s = toRad(startDeg);
  const e = toRad(endDeg);
  const sx = cx + Math.cos(s) * r;
  const sy = cy - Math.sin(s) * r;
  const ex = cx + Math.cos(e) * r;
  const ey = cy - Math.sin(e) * r;
  // Always draw the short way around. For supply (start>end ANTI-CW) the
  // sweep flag is 0 with negative direction; for demand the reverse flag
  // helps the SVG arc pick the right arm.
  const sweep = reverse ? 0 : 1;
  return (
    <path
      d={`M ${sx} ${sy} A ${r} ${r} 0 0 ${sweep} ${ex} ${ey}`}
      stroke={color}
      strokeWidth={2}
      fill="none"
    />
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

import type { CSSProperties } from "react";

const wrap: CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const tooltipBar: CSSProperties = {
  fontFamily: "SF Mono, Menlo, monospace",
  fontSize: 11,
  letterSpacing: "0.04em",
  color: "rgba(255,255,255,0.72)",
  padding: "8px 12px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 4,
  minHeight: 28,
  lineHeight: 1.4,
};

const legendRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px 16px",
  fontFamily: "SF Mono, Menlo, monospace",
  fontSize: 10,
  letterSpacing: "0.06em",
  color: "rgba(255,255,255,0.65)",
  padding: "6px 12px",
  alignItems: "center",
};

const legendLabel: CSSProperties = {
  fontWeight: 700,
  letterSpacing: "0.18em",
  color: "rgba(255,255,255,0.5)",
  marginRight: 4,
};

const legendItem: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
};
